/**
 * Voile Bleue — AJAX search bar
 * Custom element <vb-search-bar> using Shopify's predictive search endpoint.
 */
(() => {
  if (customElements.get('vb-search-bar')) return;

  const MIN_QUERY_LEN = 2;
  const DEBOUNCE_MS = 180;
  const ENDPOINT = '/search/suggest.json';
  const RESOURCE_LIMIT = 6;

  class VBSearchBar extends HTMLElement {
    constructor() {
      super();
      this._debounceId = null;
      this._abortController = null;
      this._cache = new Map();
      this._items = [];
      this._activeIndex = -1;
      this._closeTimer = null;
      this._onDocPointerDown = this._onDocPointerDown.bind(this);
      this._onFocusIn = this._onFocusIn.bind(this);
      this._onFocusOut = this._onFocusOut.bind(this);
      this._onKey = this._onKey.bind(this);
    }

    connectedCallback() {
      this.input = this.querySelector('[data-input]');
      this.clearBtn = this.querySelector('[data-clear]');
      this.panel = this.querySelector('[data-panel]');
      this.loader = this.querySelector('[data-loader]');
      this.columnsEl = this.querySelector('[data-columns]');
      this.defaultEl = this.querySelector('[data-default]');
      this.collectionsCol = this.querySelector('[data-section="collections"]');
      this.productsCol = this.querySelector('[data-section="products"]');
      this.collectionsList = this.querySelector('[data-list="collections"]');
      this.productsList = this.querySelector('[data-list="products"]');
      this.emptyEl = this.querySelector('[data-empty]');
      this.emptyQuery = this.querySelector('[data-empty-query]');
      this.cta = this.querySelector('[data-cta]');
      this.ctaWrap = this.querySelector('[data-cta-wrap]') || this.cta;
      this.ctaQuery = this.querySelector('[data-cta-query]');
      this.form = this.querySelector('form');

      this.currency = this.dataset.currency || 'EUR';
      this.locale = (this.dataset.locale || 'fr-FR').replace('_', '-');

      this.input.addEventListener('input', () => this._onInput());
      this.input.addEventListener('keydown', this._onKey);
      this.clearBtn.addEventListener('click', () => this._clear());

      // focusin/focusout bubble — they fire when focus enters/leaves any descendant.
      // This is more reliable than `focus`/`blur` + document `click`, and avoids the
      // first-click race where the click bubbles to document before focus settles.
      this.addEventListener('focusin', this._onFocusIn);
      this.addEventListener('focusout', this._onFocusOut);

      // Backup: clicks on inert nodes (no focusable target) won't fire focus events,
      // so we still need a way to close the panel. Use pointerdown on document and
      // ignore pointerdowns inside our element.
      document.addEventListener('pointerdown', this._onDocPointerDown);
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this._onDocPointerDown);
      if (this._abortController) this._abortController.abort();
      clearTimeout(this._debounceId);
      clearTimeout(this._closeTimer);
    }

    _onInput() {
      const q = this.input.value.trim();
      this.clearBtn.hidden = q.length === 0;
      clearTimeout(this._debounceId);

      if (q.length < MIN_QUERY_LEN) {
        if (this._hasDefault() && document.activeElement === this.input) {
          this._showDefault();
        } else {
          this._hidePanel();
        }
        return;
      }

      this._debounceId = setTimeout(() => this._fetch(q), DEBOUNCE_MS);
    }

    _onFocusIn() {
      // Focus entered the search bar. Cancel any pending close.
      clearTimeout(this._closeTimer);
      const q = this.input.value.trim();
      if (q.length >= MIN_QUERY_LEN) {
        this._showPanel();
      } else if (this._hasDefault()) {
        this._showDefault();
      }
    }

    _onFocusOut(e) {
      // Defer the contains() check to the next tick so e.relatedTarget /
      // document.activeElement reflects the new focus target reliably.
      clearTimeout(this._closeTimer);
      this._closeTimer = setTimeout(() => {
        if (!this.contains(document.activeElement)) {
          this._hidePanel();
        }
      }, 0);
    }

    _onDocPointerDown(e) {
      if (this.panel.hidden) return;
      if (this.contains(e.target)) return;
      this._hidePanel();
    }

    _hasDefault() {
      return this.defaultEl && this.defaultEl.querySelector('[data-vb-item]');
    }

    _showDefault() {
      if (!this._hasDefault()) return;
      if (this.columnsEl) this.columnsEl.hidden = true;
      this.defaultEl.hidden = false;
      this.emptyEl.hidden = true;
      this.ctaWrap.hidden = true;
      this._showPanel();
      this._items = Array.from(this.defaultEl.querySelectorAll('[data-vb-item]'));
      this._activeIndex = -1;
    }

    _onKey(e) {
      if (e.key === 'Escape') {
        if (this.panel.hidden) return;
        e.preventDefault();
        this._hidePanel();
        this.input.blur();
        return;
      }
      if (this.panel.hidden || !this._items.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this._move(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this._move(-1);
      } else if (e.key === 'Enter') {
        if (this._activeIndex >= 0) {
          e.preventDefault();
          window.location.href = this._items[this._activeIndex].href;
        }
      }
    }

    _move(dir) {
      this._items.forEach(el => el.classList.remove('is-active'));
      this._activeIndex = (this._activeIndex + dir + this._items.length) % this._items.length;
      const el = this._items[this._activeIndex];
      el.classList.add('is-active');
      el.scrollIntoView({ block: 'nearest' });
    }

    async _fetch(q) {
      if (this._cache.has(q)) {
        this._render(q, this._cache.get(q));
        return;
      }
      if (this._abortController) this._abortController.abort();
      this._abortController = new AbortController();

      this._showPanel();
      this.loader.hidden = false;

      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection',
        'resources[limit]': String(RESOURCE_LIMIT),
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,vendor,tag'
      });

      try {
        const res = await fetch(`${ENDPOINT}?${params.toString()}`, {
          signal: this._abortController.signal,
          headers: { 'Accept': 'application/json' }
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        this._cache.set(q, data);
        this._render(q, data);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.warn('[vb-search-bar]', err);
          this._renderError();
        }
      } finally {
        this.loader.hidden = true;
      }
    }

    _render(q, data) {
      const products = (data.resources && data.resources.results && data.resources.results.products) || [];
      const collections = (data.resources && data.resources.results && data.resources.results.collections) || [];

      if (this.defaultEl) this.defaultEl.hidden = true;
      if (this.columnsEl) this.columnsEl.hidden = false;

      this.collectionsList.innerHTML = collections.slice(0, RESOURCE_LIMIT).map(c => this._renderCollection(c, q)).join('');
      this.productsList.innerHTML = products.slice(0, RESOURCE_LIMIT).map(p => this._renderProduct(p, q)).join('');

      this.collectionsCol.hidden = collections.length === 0;
      this.productsCol.hidden = products.length === 0;

      const hasResults = products.length > 0 || collections.length > 0;
      this.emptyEl.hidden = hasResults;
      if (!hasResults) this.emptyQuery.textContent = q;

      this.ctaWrap.hidden = !hasResults;
      this.ctaQuery.textContent = q;

      this._items = Array.from(this.querySelectorAll('[data-vb-item]'));
      this._activeIndex = -1;
    }

    _renderError() {
      if (this.defaultEl) this.defaultEl.hidden = true;
      if (this.columnsEl) this.columnsEl.hidden = true;
      this.collectionsCol.hidden = true;
      this.productsCol.hidden = true;
      this.ctaWrap.hidden = true;
      this.emptyEl.hidden = false;
      this.emptyQuery.textContent = '…';
    }

    _renderCollection(c, q) {
      const url = c.url || `/collections/${c.handle}`;
      return `
        <li role="presentation">
          <a href="${url}" class="vb-search-bar__item vb-search-bar__item--collection" role="option" data-vb-item>
            <span class="vb-search-bar__item-icon" aria-hidden="true">›</span>
            <span class="vb-search-bar__item-title">${this._highlight(c.title, q)}</span>
          </a>
        </li>`;
    }

    _renderProduct(p, q) {
      const url = p.url || `/products/${p.handle}`;
      const img = this._productImage(p);
      const vendor = p.vendor ? `<span class="vb-search-bar__item-vendor">${this._escape(p.vendor)}</span>` : '';
      const price = this._priceMarkup(p);
      return `
        <li role="presentation">
          <a href="${url}" class="vb-search-bar__item vb-search-bar__item--product" role="option" data-vb-item>
            <span class="vb-search-bar__item-image">${img}</span>
            <span class="vb-search-bar__item-info">
              <span class="vb-search-bar__item-title">${this._highlight(p.title, q)}</span>
              ${vendor}
              <span class="vb-search-bar__item-price">${price}</span>
            </span>
          </a>
        </li>`;
    }

    _productImage(p) {
      const src = (p.featured_image && p.featured_image.url) || p.image || (p.images && p.images[0]) || '';
      if (!src) return '';
      const url = src.includes('?') ? `${src}&width=120` : `${src}${src.includes('cdn.shopify.com') ? '&' : '?'}width=120`;
      return `<img src="${this._escape(url)}" loading="lazy" alt="" width="56" height="56">`;
    }

    _priceMarkup(p) {
      const price = typeof p.price === 'number' ? p.price : (p.price_min || p.price);
      const compare = typeof p.compare_at_price === 'number' ? p.compare_at_price : (p.compare_at_price_min || 0);
      if (compare && compare > price) {
        return `<del>${this._formatMoney(compare)}</del><ins>${this._formatMoney(price)}</ins>`;
      }
      return this._formatMoney(price);
    }

    _formatMoney(value) {
      if (value == null || value === '') return '';
      const num = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
      if (Number.isNaN(num)) return this._escape(String(value));
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: 2
        }).format(num);
      } catch (_) {
        return `${num.toFixed(2)} ${this.currency}`;
      }
    }

    _highlight(text, q) {
      const safe = this._escape(text || '');
      if (!q) return safe;
      const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return safe.replace(new RegExp(`(${escaped})`, 'ig'), '<mark>$1</mark>');
    }

    _escape(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    _showPanel() {
      this.panel.hidden = false;
      this.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
    }

    _hidePanel() {
      this.panel.hidden = true;
      this.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      this._activeIndex = -1;
      this._items.forEach(el => el.classList.remove('is-active'));
    }

    _clear() {
      this.input.value = '';
      this.clearBtn.hidden = true;
      this._hidePanel();
      this.input.focus();
    }
  }

  customElements.define('vb-search-bar', VBSearchBar);
})();
